import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { usePlaceLang, altLinks } from "@/lib/placeLang";
import { LocalLawyers } from "@/components/LocalLawyers";
import api from "@/lib/api";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { getCounty } from "@/lib/ncCounties";
import countyContent from "@/lib/countyContent.json";
import topForms from "@/lib/topForms.json";
import { categoryTint } from "@/lib/categoryColors";
import { CountyCities } from "@/components/PlaceLinks";
import { Landmark, ArrowRight, Sparkles, ExternalLink, MapPin, HelpCircle, FileText } from "lucide-react";
import { pageText } from "@/lib/i18n/pages";
import { trackView } from "@/lib/trackView";
import { PLACE_SEO } from "@/lib/i18n/placeSeo";

const TOPICS = [
  "Absolute Divorce", "Child Custody", "Child Support", "Domestic Violence Protective Order",
  "Small Claims", "Eviction (Summary Ejectment)", "Name Change", "Estate & Probate",
  "Expunction", "Civil Summons", "Guardianship", "Traffic & Criminal",
];

const SITE = process.env.REACT_APP_BACKEND_URL || "";

const TOPICS_ES = {
  "Absolute Divorce": "Divorcio absoluto", "Child Custody": "Custodia de menores", "Child Support": "Manutención de menores",
  "Domestic Violence Protective Order": "Orden de protección por violencia doméstica", "Small Claims": "Reclamos menores",
  "Eviction (Summary Ejectment)": "Desalojo", "Name Change": "Cambio de nombre", "Estate & Probate": "Herencias y sucesiones",
  "Expunction": "Eliminación de antecedentes", "Civil Summons": "Citación civil", "Guardianship": "Tutela", "Traffic & Criminal": "Tránsito y penal",
};

const T = {
  en: {
    nc: "North Carolina Counties", county: (c) => `${c} County`, seat: "County seat", h1: (c) => `${c} County, North Carolina Court Forms`,
    find: "Find my forms with AI", browse: "Browse all forms", popular: (c) => `Popular forms filed in ${c} County`,
    filing: (c) => `Filing at the ${c} County Courthouse`,
    filingBody: (c, s) => `Court forms are filed with the Clerk of Superior Court in ${s}, the ${c} County seat. Fees, e-filing availability and local rules vary by county — confirm current details and the courthouse address with the official North Carolina Judicial Branch before you file.`,
    courthouse: (c) => `Find the ${c} County courthouse on nccourts.gov`, top: (c) => `Top forms filed in ${c} County`,
    topSub: (c) => `Jump straight to the most-requested North Carolina court forms — fill, autofill and print them for your ${c} County case.`,
    faq: (c) => `${c} County filing questions`, topic: (t) => t, place: (c) => `${c} County`,
    why: (c) => `Why file ${c} County forms with NC Legal Forms?`,
    whyList: [
      "Every official AOC form, organized by category and always the current version.",
      "AI reads your plain-language situation and recommends the right forms.",
      "Autofill your name, address, county and case number across every form.",
      "Fill online with a live preview of the real court PDF, then print or download.",
    ],
    disclaimer: "NC Legal Forms is not affiliated with the North Carolina Judicial Branch and does not provide legal advice.",
  },
  es: {
    nc: "Condados de Carolina del Norte", county: (c) => `Condado de ${c}`, seat: "Sede del condado", h1: (c) => `Formularios judiciales del condado de ${c}, Carolina del Norte`,
    find: "Encontrar mis formularios con IA", browse: "Ver todos los formularios", popular: (c) => `Formularios populares en el condado de ${c}`,
    filing: (c) => `Cómo presentar en el tribunal del condado de ${c}`,
    filingBody: (c, s) => `Los formularios se presentan ante el Secretario del Tribunal Superior en ${s}, sede del condado de ${c}. Las tarifas, la presentación electrónica y las reglas locales varían por condado; confirme los detalles y la dirección del tribunal con el Poder Judicial de Carolina del Norte antes de presentar.`,
    courthouse: (c) => `Ver el tribunal del condado de ${c} en nccourts.gov`, top: (c) => `Formularios más usados en el condado de ${c}`,
    topSub: (c) => `Vaya directo a los formularios judiciales más solicitados de Carolina del Norte para su caso en el condado de ${c}.`,
    faq: (c) => `Preguntas sobre trámites en el condado de ${c}`, topic: (t) => TOPICS_ES[t] || t, place: (c) => `el condado de ${c}`,
    why: (c) => `¿Por qué preparar sus formularios del condado de ${c} con NC Legal Forms?`,
    whyList: [
      "Todos los formularios oficiales AOC, organizados por categoría y siempre en su versión vigente.",
      "La IA entiende su situación en palabras sencillas y le recomienda los formularios correctos.",
      "Autocomplete su nombre, dirección, condado y número de caso en todos los formularios.",
      "Llene en línea con una vista previa del PDF oficial y luego imprima o descargue.",
    ],
    disclaimer: "NC Legal Forms no está afiliado al Poder Judicial de Carolina del Norte y no ofrece asesoría legal.",
  },
};

export default function CountyForms() {
  const { slug } = useParams();
  const { lang, base, ready } = usePlaceLang();
  useEffect(() => { if (ready) trackView({ kind: "county", lang, county: slug }); }, [ready, lang, slug]);
  const L = pageText(lang, "county") || (lang === "es" ? T.es : T.en);
  const [esContent, setEsContent] = useState(null);
  useEffect(() => {
    setEsContent(null);
    if (lang !== "en") api.get(`/places/counties/${slug}/guide`, { params: { lang } }).then(({ data }) => setEsContent(data)).catch(() => {});
  }, [slug, lang]);
  const county = getCounty(slug);
  if (!county) return <Navigate to={base} replace />;
  const content = (lang !== "en" && esContent) || countyContent[slug];
  const faqs = content?.faqs || [];

  const title = PLACE_SEO[lang] ? PLACE_SEO[lang].countyTitle(county) : lang === "es"
    ? `Formularios judiciales del condado de ${county.name}, NC — Llene, imprima y presente | NC Legal Forms`
    : `${county.name} County, NC Court Forms — Fill, Print & File Online | NC Legal Forms`;
  const desc = PLACE_SEO[lang] ? PLACE_SEO[lang].countyDesc(county) : lang === "es"
    ? `Formularios oficiales del Poder Judicial de Carolina del Norte (AOC) para el condado de ${county.name} (sede: ${county.seat}). Llénelos en línea con IA e imprima PDFs listos para el tribunal: divorcio, custodia, manutención, reclamos menores, desalojos y más.`
    : `Complete official North Carolina Judicial Branch (AOC) court forms for ${county.name} County (county seat: ${county.seat}). Fill online with AI, autofill your info, print and download court-ready PDFs for divorce, custody, child support, small claims, evictions and more.`;
  const url = `${SITE}${base}/${county.slug}`;

  return (
    <div>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        {altLinks(SITE, `/${county.slug}`).map((l) => <link key={l.hrefLang} rel="alternate" hrefLang={l.hrefLang} href={l.href} />)}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "GovernmentService",
          "name": `${county.name} County North Carolina Court Forms`,
          "serviceType": "Court form preparation",
          "areaServed": { "@type": "AdministrativeArea", "name": `${county.name} County, North Carolina` },
          "provider": { "@type": "Organization", "name": "NC Legal Forms" },
          "url": url,
        })}</script>
        {faqs.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((f) => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
          })}</script>
        )}
      </Helmet>
      <Navbar />

      <section className="max-w-5xl mx-auto px-6 pt-14 pb-10" data-testid="county-page">
        <nav className="text-sm text-muted-foreground mb-6 flex items-center gap-2">
          <Link to={base} className="hover:text-accent">{L.nc}</Link>
          <span>/</span><span className="text-foreground">{L.county(county.name)}</span>
        </nav>
        <p className="overline text-accent mb-3 flex items-center gap-2"><MapPin size={14} /> {L.seat} · {county.seat}</p>
        <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6">
          {L.h1(county.name)}
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed" data-testid="county-intro">
          {content?.intro || (
            <>Filing at the {county.name} County Courthouse in {county.seat}? Complete the exact official
            North Carolina Judicial Branch (AOC) forms you need — fill them online, let AI map your details
            to the right fields, autofill your saved info, then print or download a court-ready PDF. Every
            form is the current statewide version accepted in all 100 NC counties, including {county.name}.</>
          )}
        </p>
        <div className="flex flex-wrap gap-3 mt-8">
          <Link to="/find"><Button className="rounded-sm gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors"><Sparkles size={16} /> {L.find}</Button></Link>
          <Link to="/forms"><Button variant="outline" className="rounded-sm gap-2 hover:bg-secondary">{L.browse} <ArrowRight size={16} /></Button></Link>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-8">
        <h2 className="font-serif text-3xl tracking-tight mb-6">{L.popular(county.name)}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TOPICS.map((topic) => (
            <Link key={topic} to={`/forms?q=${encodeURIComponent(topic)}`} data-testid={`county-topic-${topic}`}
              style={{ backgroundColor: categoryTint(topic) }}
              className="border-[3px] border-black p-5 flex items-center justify-between group text-black transition-transform hover:-translate-y-1">
              <span className="font-serif font-semibold text-lg leading-tight pr-2">{L.topic(topic)}</span>
              <ArrowRight size={18} className="shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid-card p-8">
          <Landmark className="text-accent mb-3" />
          <h2 className="font-serif text-2xl tracking-tight mb-2">{L.filing(county.name)}</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">{L.filingBody(county.name, county.seat)}</p>
          <a href={`https://www.nccourts.gov/locations?county=${encodeURIComponent(county.name)}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 text-accent hover:underline font-medium" data-testid="county-official-link">
            {L.courthouse(county.name)} <ExternalLink size={15} />
          </a>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-6" data-testid="county-top-forms">
        <h2 className="font-serif text-3xl tracking-tight mb-2">{L.top(county.name)}</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl">{L.topSub(county.name)}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {topForms.map((f) => (
            <Link key={f.number} to={`/forms/${f.number}`} data-testid={`county-form-${f.number}`}
              className="grid-card p-4 flex items-center justify-between gap-3 group">
              <div className="min-w-0">
                <p className="text-[0.7rem] uppercase tracking-wider text-accent font-semibold">{f.topic}</p>
                <p className="font-serif text-base leading-tight truncate">{f.title}</p>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">{f.number}</p>
              </div>
              <FileText size={18} className="text-muted-foreground shrink-0 group-hover:text-accent transition-colors" />
            </Link>
          ))}
        </div>
      </section>

      <CountyCities countySlug={county.slug} countyName={county.name} lang={lang} />
      <LocalLawyers county={county.name} place={L.place(county.name)} lang={lang} />

      {faqs.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 py-6" data-testid="county-faq">
          <h2 className="font-serif text-3xl tracking-tight mb-6 flex items-center gap-2">
            <HelpCircle className="text-accent" /> {L.faq(county.name)}
          </h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f, i) => (
              <div key={i} className="grid-card p-6" data-testid={`county-faq-${i}`}>
                <h3 className="font-serif text-lg leading-snug mb-2">{f.q}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-6 pb-16">
        <h2 className="font-serif text-2xl tracking-tight mb-4">{L.why(county.name)}</h2>
        <ul className="space-y-3 text-muted-foreground max-w-3xl">
          {L.whyList.map((line) => (
            <li key={line} className="flex items-start gap-3"><span className="w-1.5 h-1.5 bg-accent rounded-full mt-2.5 shrink-0" />{line}</li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground mt-6">{L.disclaimer}</p>
      </section>

      <Footer />
    </div>
  );
}
